/**
 * MobileRequestsList Component
 *
 * Mobile-optimized view for displaying and filtering requests by status and priority.
 * Supports navigation from dashboard with special handling for blocker requests.
 *
 * This component includes additional filtering logic at the component level to ensure
 * correct display regardless of the QueueContext state. It handles various navigation
 * scenarios including direct links from dashboard stats and reconciles URL parameters
 * with context state.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback, createRef } from 'react';

// Feature flag for showing dedicated Blocker tab
// Set to false to show blockers at the top of each status group instead
const SHOW_BLOCKER_TAB = false;

// Feature flag for using new blockers approach
// Keep the old code path for initial tab setup but still use the new rendering approach
const USE_NEW_BLOCKERS_APPROACH = true;
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import MobileActionModal from 'presentation/components/Mobile/MobileActionModal/MobileActionModal'; // Import the modal
import { prefixClass } from 'lib/utils';
import RBSearch from 'presentation/components/Queue/RequestsBrowser/RBSearch/RBSearch';
import AssignmentGroup from 'presentation/components/Queue/AssignmentGroup/AssignmentGroup';
import MobileRequestStatusTabs, { ActiveFilterType } from './MobileRequestStatusTabs/MobileRequestStatusTabs';
import {
  IconAdd,
  IconAddNew,
  IconMention,
  IconMentionPurple,
  IconPlus,
  IconSort,
  IconSortTime,
} from 'components/Icons';
import { FilterProps } from 'presentation/components/Queue/utils/filterAndSearchUtils';
import { splitRequestsByPriority } from 'presentation/components/Queue/utils/filterAndSearchUtils';
import { filterAndSplitByPriority } from 'presentation/components/Queue/utils/componentFilteringUtils';
import './MobileRequestsList.scss';
import { QueueCreateAskReq } from 'presentation/components/AskRequest';
import { MobileSlideUpPanel } from 'presentation/components/Mobile/MobileSlideUpPanel/MobileSlideUpPanel';
import { useLayoutContext } from 'context/LayoutContext'; // Added import
import { useAuthContext } from 'context/useAuthContext';

type Props = {
  readReport?: any; // Placeholder type
  trace?: any; // Placeholder type
  onShowDetails?: (id: string) => void; // Update prop to accept request ID
};

type StatusFilterParam = 'Open' | 'In Progress' | null;

type ValidStatusFilter = FilterProps['status'];

const CustomBrowserColumn: React.FC<Props> = ({ readReport, trace, onShowDetails }) => {
  const prefix = prefixClass('mobile-requests-list');
  const { state, dispatch } = useQueueContext();
  const navigate = useNavigate();
  const { userId } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [createRequestModalOpen, setCreateRequestModalOpen] = React.useState<boolean>(false);
  // State for Sort modal
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const filterMentionsEnabled = searchParams.get('filterMentions') === 'true';

  // Ref for the sort button to position the modal
  const sortButtonRef = useRef<HTMLButtonElement>(null);

  // Handler for search input
  const handleSearch = (value: string) => {
    dispatch({
      type: 'SET_FILTERS',
      payload: { ...state.filters, search: value },
    });
  };

  // Handler for clearing search
  const handleClear = () => {
    dispatch({
      type: 'SET_FILTERS',
      payload: { ...state.filters, search: '' },
    });
  };

  // Handler for tab clicks
  const handleTabChange = (newFilter: ActiveFilterType) => {
    let newStatusParam: ValidStatusFilter | null = null;
    let newPriorityParam: 'Blocker' | null = null;
    let targetActiveTab: ActiveFilterType = newFilter;
    // Default sort order based on tab
    let newSortOrder: 'oldest' | 'newest' = 'oldest';

    if (newFilter === 'Blocker' && !SHOW_BLOCKER_TAB) {
      // Find the first status tab that has blockers, in priority order: To Do, In Progress, Resolved
      if (requestsTodo.length > 0) {
        targetActiveTab = 'Open';
        newStatusParam = 'Open';
        newSortOrder = 'oldest'; // Oldest first for Open
      } else if (requestsInProgress.length > 0) {
        targetActiveTab = 'In Progress';
        newStatusParam = 'In Progress';
        newSortOrder = 'oldest'; // Oldest first for In Progress
      } else if (requestsClosed.length > 0) {
        targetActiveTab = 'Closed';
        newStatusParam = 'Closed';
        newSortOrder = 'newest'; // Newest first for Closed
      } else {
        // No blockers found, default to Open
        targetActiveTab = 'Open';
        newStatusParam = 'Open';
        newSortOrder = 'oldest'; // Oldest first for Open
      }
    } else if (newFilter === 'Blocker' && SHOW_BLOCKER_TAB) {
      // Original behavior for Blocker tab when feature flag is true
      newPriorityParam = 'Blocker';
      newStatusParam = 'Open'; // Keep a base status when viewing blockers
      newSortOrder = 'oldest'; // Oldest first for Blockers
    } else if (newFilter === 'Open' || newFilter === 'In Progress' || newFilter === 'Closed') {
      // For status tabs, set both the active tab and status parameter
      newStatusParam = newFilter;
      // Set default sort order based on status tab
      if (newFilter === 'Closed') {
        newSortOrder = 'newest'; // Newest first for Closed
      } else {
        newSortOrder = 'oldest'; // Oldest first for Open and In Progress
      }
    }

    // Set the active tab to reflect the correct status
    setActiveTabFilter(targetActiveTab);

    // Update context state with both status and sort order
    dispatch({
      type: 'SET_FILTERS',
      payload: {
        ...state.filters,
        status: newStatusParam || 'Open', // Ensure status is never null in context
        sort: newSortOrder, // Apply the appropriate sort order
      },
    });

    // Update URL Search Parameters
    const params = new URLSearchParams(searchParams);
    if (newPriorityParam) {
      params.set('priority', newPriorityParam);
    } else {
      params.delete('priority');
    }
    if (newStatusParam) {
      params.set('status', newStatusParam);
    } else {
      params.delete('status');
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Handlers for Sort Modal
  const handleSortClick = () => {
    setIsSortModalOpen(true);
  };

  // Handler for selecting a sort option in the modal
  const handleSortSelect = (sortOption: 'oldest' | 'newest') => {
    // First update URL to ensure it's synchronized
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', sortOption);
    navigate(`?${newParams.toString()}`, { replace: true });

    // Then update the context state
    dispatch({ type: 'SET_FILTERS', payload: { ...state.filters, sort: sortOption } });

    // Close modal after selection
    setIsSortModalOpen(false);
  };

  // Handler for clicking on a request item in the list
  const handleRequestClick = useCallback(
    (id: string) => {
      // Find the request object from the main list
      const clickedRequest = state.requests.find(req => req.id === id);

      if (clickedRequest) {
        // Update the context with the selected item
        dispatch({ type: 'SET_SELECTED_ITEM', payload: clickedRequest });

        // Call the prop function to show the details panel
        if (onShowDetails) {
          onShowDetails(id);
        } else {
          console.warn('MobileRequestsList: onShowDetails prop is not provided.');
        }
      } else {
        console.error(`MobileRequestsList: Could not find request with id: ${id}`);
        // Optionally dispatch SET_SELECTED_ITEM with null here if needed
        // dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
      }
    },
    [state.requests, onShowDetails, dispatch]
  );

  // Handler for Create Request Modal
  const toggleCreateRequestModal = () => {
    setCreateRequestModalOpen(val => !val);
  };

  // Get initial filter from URL when component is first created (no flashing)
  const initialFilter: ActiveFilterType = (() => {
    const statusParam = searchParams.get('status') as ActiveFilterType | null;
    const priorityParam = searchParams.get('priority');

    if (priorityParam === 'Blocker' && SHOW_BLOCKER_TAB) {
      return 'Blocker';
    } else if (statusParam && ['Open', 'In Progress', 'Closed'].includes(statusParam)) {
      return statusParam;
    }
    return 'Open'; // Default if no valid params
  })();

  // Component state - initialized with URL parameters to avoid flashing
  const [activeTabFilter, setActiveTabFilter] = useState<ActiveFilterType>(initialFilter);

  // Critical fix: This effect updates the QueueContext filters with the initial status from URL
  // when navigating directly from the dashboard stats (Open, In Progress, etc.)
  useEffect(() => {
    // Get the status from URL
    const statusFromUrl = searchParams.get('status') as FilterProps['status'];

    // Only update if there's a valid status in URL that doesn't match current filters
    if (
      statusFromUrl &&
      ['Open', 'In Progress', 'Closed'].includes(statusFromUrl) &&
      statusFromUrl !== state.filters.status
    ) {
      // Status from URL doesn't match context, updating

      // Update the context filters with the status from URL
      dispatch({
        type: 'SET_FILTERS',
        payload: { ...state.filters, status: statusFromUrl },
      });
    }
  }, []); // Empty dependency array = run only once on mount

  // Reference to the requests browser container for scrolling
  const requestsBrowserRef = useRef<HTMLDivElement>(null);

  // Add CSS for highlighting the selected request
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    style.innerHTML = `
      .highlight-request {
        animation: pulse 0.4s;
      }
      @keyframes pulse {
        0% { background-color: var(--ks-dashboard-selected-item); }
        100% { background-color: transparent; }
      }
    `;
    document.head.appendChild(style);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Check if there are *any* blocker requests in the raw data, regardless of filters
  const hasBlockers = useMemo(
    () =>
      // Check for 'priority' field, mirroring splitRequestsByPriority (which uses @ts-ignore)
      // This assumes 'priority' exists at runtime despite type definition mismatch.
      state.requests.some(req => (req as any).priority === 'Blocker'),
    [state.requests] // Depend only on the raw request list
  );

  // Get initial filter from URL or default
  const initialFilterFromUrl = searchParams.get('status') as ActiveFilterType | null;

  // Requests split by status within the 'Blocker' group (already filtered by context search)
  const requestsTodo = useMemo(
    () => state.priorityGroups.Blocker.filter(req => req.status === 'Open'),
    [state.priorityGroups.Blocker]
  );
  const requestsInProgress = useMemo(
    () => state.priorityGroups.Blocker.filter(req => req.status === 'In Progress'),
    [state.priorityGroups.Blocker]
  );
  const requestsClosed = useMemo(
    () => state.priorityGroups.Blocker.filter(req => req.status === 'Closed'),
    [state.priorityGroups.Blocker]
  );
  const mentionedRequest = state.requests.filter(
    req => req.status === activeTabFilter && req.comments?.some(comment => comment.taggedUserIds?.includes(userId!))
  );

  // Read query parameters
  const priorityFilter = searchParams.get('priority');

  useEffect(() => {
    const requestIdFromUrl = searchParams.get('requestId');
    // Only proceed if requestId exists in URL and requests are loaded in context
    if (requestIdFromUrl && state.requests && state.requests.length > 0) {
      // Check requests array directly
      // Find the request in the context's list
      const requestFromContext = state.requests.find(req => req.id === requestIdFromUrl);

      // If found and it's not already the selected item, dispatch action
      if (requestFromContext && state.selectedItem?.id !== requestIdFromUrl) {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: requestFromContext });
      } else if (!requestFromContext) {
        console.warn(`MobileRequestsList: Request ID ${requestIdFromUrl} from URL not found in QueueContext.`);
        // Optionally clear URL param if request not found? Depends on desired UX.
        // For now, just log a warning.
      }

      // Scroll to the selected request after a short delay to ensure the DOM is updated
      setTimeout(() => {
        // Find the request item by its data attribute
        const requestElement = document.querySelector(`[data-request-id="${requestIdFromUrl}"]`);

        if (requestElement) {
          // Scroll the element into view without animation
          requestElement.scrollIntoView({ block: 'center' });

          // Add a temporary highlight class
          requestElement.classList.add('highlight-request');

          // Remove the highlight class after animation completes
          setTimeout(() => {
            requestElement.classList.remove('highlight-request');
          }, 400); // 2 seconds highlight duration
        }
      }, 200); // Short delay to ensure DOM is updated
    }
    // Dependencies: searchParams (to react to URL changes), state.requests (to react to data loading), dispatch
  }, [searchParams, state.requests, state.selectedItem, dispatch]); // Use state.requests in dependency array

  // Flags to track if we've already processed redirection logic
  const hasProcessedInitialBlockerRef = useRef(false);
  const hasProcessedRequestIdRef = useRef(false);
  const hasProcessedDashboardNavRef = useRef(false);

  // Effect to handle coming from dashboard by clearing requestId
  useEffect(() => {
    // Only run once when component mounts and only if we have a referrer
    if (!hasProcessedDashboardNavRef.current && document.referrer) {
      hasProcessedDashboardNavRef.current = true;

      // Check if we're coming from the dashboard (referrer contains 'new-dashboard' but not 'view=queue')
      const isFromDashboard = document.referrer.includes('new-dashboard') && !document.referrer.includes('view=queue');

      // If coming from dashboard, clear any requestId
      if (isFromDashboard && searchParams.has('requestId')) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('requestId');
        navigate(`?${newParams.toString()}`, { replace: true });
        return; // Exit early as we're replacing the URL
      }
    }
  }, [searchParams, navigate]);

  // Effect to handle requestId in URL by switching to appropriate tab
  useEffect(() => {
    // Only run once the data is loaded and we haven't processed it yet
    if (state.requests.length === 0 || hasProcessedRequestIdRef.current) {
      return;
    }

    const requestIdFromUrl = searchParams.get('requestId');

    // If there's a requestId in the URL
    if (requestIdFromUrl && state.requests.some(req => req.id === requestIdFromUrl)) {
      // Mark as processed
      hasProcessedRequestIdRef.current = true;

      // Find the request to determine its status
      const request = state.requests.find(req => req.id === requestIdFromUrl);

      if (request) {
        // Determine which tab should show this request
        let targetStatus: string;
        if (request.status === 'Open') {
          targetStatus = 'Open';
        } else if (request.status === 'In Progress') {
          targetStatus = 'In Progress';
        } else {
          targetStatus = 'Closed';
        }

        // Only update if the current status doesn't match
        if (searchParams.get('status') !== targetStatus) {
          // Update URL and state to match the request's status

          // Only set default sort order if there's no sort parameter in the URL
          // This respects the user's sort selection
          if (!searchParams.has('sort')) {
            const sortOrder = targetStatus === 'Closed' ? 'newest' : 'oldest';
            dispatch({
              type: 'SET_FILTERS',
              payload: { ...state.filters, sort: sortOrder },
            });
          }
        }
      }
    }
  }, [state.requests, searchParams, navigate, state.filters, dispatch]);

  // This effect detects when component is mounted or URL location changes
  // It's used to reset the blocker processing flag when navigating away and back
  useEffect(() => {
    // Reset the flag whenever we navigate to this component
    // This ensures dashboard->request list navigation always works
    hasProcessedInitialBlockerRef.current = false;

    // Clean up when component unmounts
    return () => {
      // Extra safety - reset the flag when unmounting too
      hasProcessedInitialBlockerRef.current = false;
    };
  }, [location.pathname]); // Only run when the path changes (navigating to/from this page)

  // Effect to handle priority=Blocker from dashboard navigation
  useEffect(() => {
    // Wait for data to be loaded
    if (state.requests.length === 0) {
      return;
    }

    let targetTab: ActiveFilterType = 'Open'; // Default
    let sortOrder: 'oldest' | 'newest' = 'oldest';

    // Special case: Coming from dashboard blockers (priority=Blocker in URL)
    if (priorityFilter === 'Blocker' && !SHOW_BLOCKER_TAB && !hasProcessedInitialBlockerRef.current) {
      // Process blocker navigation from dashboard
      // Mark as processed to avoid infinite loops
      hasProcessedInitialBlockerRef.current = true;

      // Use our shared utility function to ensure consistent filtering across components
      // This gets fresh data directly rather than depending on memoized values
      const openBlockers = filterAndSplitByPriority(state.requests, 'Open').blockerRequests;
      const inProgressBlockers = filterAndSplitByPriority(state.requests, 'In Progress').blockerRequests;
      const closedBlockers = filterAndSplitByPriority(state.requests, 'Closed').blockerRequests;

      // Determine which tab should be selected based on where blockers exist
      if (openBlockers.length > 0) {
        targetTab = 'Open';
        sortOrder = 'oldest';
      } else if (inProgressBlockers.length > 0) {
        targetTab = 'In Progress';
        sortOrder = 'oldest';
      } else if (closedBlockers.length > 0) {
        targetTab = 'Closed';
        sortOrder = 'newest';
      } else {
        // No blockers found, default to Open
        targetTab = 'Open';
        sortOrder = 'oldest';
      }

      // Update URL to reflect the correct status (one-time update)
      const newParams = new URLSearchParams(searchParams);
      newParams.set('status', targetTab);
      // Keep priority=Blocker in URL to maintain filtering
      navigate(`?${newParams.toString()}`, { replace: true });

      // Immediately update context with this status to ensure proper filtering
      dispatch({
        type: 'SET_FILTERS',
        payload: { ...state.filters, status: targetTab as FilterProps['status'] },
      });
    }
    // Normal case handling
    else if (priorityFilter === 'Blocker' && SHOW_BLOCKER_TAB) {
      // When showing the blocker tab, set the active tab to 'Blocker'
      targetTab = 'Blocker';
    } else if (initialFilterFromUrl) {
      // Use the status from URL if available
      targetTab = initialFilterFromUrl;

      // Set sort order based on status
      if (targetTab === 'Closed') {
        sortOrder = 'newest';
      }
    }

    // Set the tab state
    setActiveTabFilter(targetTab);

    // Only set default sort order if there's no sort parameter in the URL
    // This respects the user's sort selection
    if (!searchParams.has('sort') && state.filters.sort !== sortOrder) {
      dispatch({
        type: 'SET_FILTERS',
        payload: { ...state.filters, sort: sortOrder },
      });
    }
  }, [
    state.requests, // Changed from state.requests.length to ensure latest data
    priorityFilter,
    initialFilterFromUrl,
    SHOW_BLOCKER_TAB,
    searchParams,
    navigate,
    state.filters,
    dispatch,
  ]); // Removed dependencies on memoized values that might cause circular updates

  // Reset the processed flag when the priority filter changes
  // This allows future blocker tab visits to work correctly
  useEffect(() => {
    // Reset the flag if priority changes (allows future redirections)
    const currentPriority = searchParams.get('priority');
    if (currentPriority !== 'Blocker') {
      hasProcessedInitialBlockerRef.current = false;
    }
  }, [searchParams]);

  const { setFooterVisible } = useLayoutContext(); // Added context consumption

  // Effect to control footer visibility based on modal state
  useEffect(() => {
    if (createRequestModalOpen) {
      setFooterVisible(false);
    } else {
      setFooterVisible(true);
    }
    // Cleanup: ensure footer is visible if component unmounts while modal is open
    return () => setFooterVisible(true);
  }, [createRequestModalOpen, setFooterVisible]);

  const handleMentionsClick = () => {
    const params = new URLSearchParams(searchParams);
    if (params.get('filterMentions') === 'true') {
      params.set('filterMentions', 'false');
    } else {
      params.set('filterMentions', 'true');
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  return (
    <div className={prefix()}>
      {/* Status Tabs */}
      <MobileRequestStatusTabs
        activeFilter={activeTabFilter}
        onFilterChange={handleTabChange}
        hasBlockers={hasBlockers && SHOW_BLOCKER_TAB}
      />

      {/* Search and Action Bar */}
      <div className={prefix('action-bar')}>
        <div className={prefix('search')}>
          <RBSearch searchValue={state.filters.search || ''} onSearch={handleSearch} onClear={handleClear} />
        </div>
        <div className={prefix('action-buttons')}>
          {/* Mentions Button */}
          <button
            type="button"
            onClick={handleMentionsClick}
            className={prefix('action-button mentions')}
            aria-label="Mentions"
          >
            {filterMentionsEnabled ? <IconMentionPurple /> : <IconMention />}
          </button>
          {/* Sort Button */}
          <button
            type="button"
            ref={sortButtonRef} // Attach the ref
            onClick={handleSortClick}
            className={prefix('action-button sort')}
            aria-label="Sort Requests"
          >
            <IconSortTime />
          </button>
          <button
            type="button"
            onClick={toggleCreateRequestModal}
            className={prefix('action-button')}
            aria-label="Create Request"
          >
            <IconAddNew />
          </button>
        </div>
      </div>
      <div className={prefix('request-browser')} ref={requestsBrowserRef}>
        <div className="requests-browser-results">
          {/* New Approach - Show blockers at top of each status group */}
          {filterMentionsEnabled && mentionedRequest.length > 0 ? (
            <AssignmentGroup
              me={false}
              requests={mentionedRequest}
              reversed={state.filters.sort === 'newest'}
              onClick={handleRequestClick}
            />
          ) : (
            !SHOW_BLOCKER_TAB &&
            activeTabFilter !== 'Blocker' &&
            hasBlockers && (
              <>
                {/* Show relevant blockers for the current status tab */}
                {activeTabFilter === 'Open' && requestsTodo.length > 0 && (
                  <AssignmentGroup
                    me={false}
                    blocker
                    groupLabel="Blockers"
                    requests={requestsTodo}
                    reversed={false}
                    onClick={handleRequestClick}
                  />
                )}
                {activeTabFilter === 'In Progress' && requestsInProgress.length > 0 && (
                  <AssignmentGroup
                    me={false}
                    blocker
                    groupLabel="Blockers"
                    requests={requestsInProgress}
                    reversed={false}
                    onClick={handleRequestClick}
                  />
                )}
                {activeTabFilter === 'Closed' && requestsClosed.length > 0 && (
                  <AssignmentGroup
                    me={false}
                    blocker
                    groupLabel="Blockers"
                    requests={requestsClosed}
                    reversed={true}
                    onClick={handleRequestClick}
                  />
                )}
              </>
            )
          )}

          {/* Regular Requests Group - Only show when not on Blocker tab */}
          {(activeTabFilter !== 'Blocker' || !SHOW_BLOCKER_TAB) && !filterMentionsEnabled && (
            <AssignmentGroup
              me={false}
              requests={state.filteredNormalRequests}
              reversed={state.filters.status === 'Closed'} // Use context status filter
              onClick={handleRequestClick}
              groupLabel={!SHOW_BLOCKER_TAB ? activeTabFilter : undefined} // Add group label in new approach
            />
          )}
        </div>
      </div>
      {/* Create Ask Request Modal - Using MobileSlideUpPanel */}
      <MobileSlideUpPanel
        isOpen={createRequestModalOpen}
        onClose={toggleCreateRequestModal}
        title="Create Request"
        fullSizeOnOpen={true}
      >
        <div className="mobile-create-request-wrapper">
          <QueueCreateAskReq isOpen={createRequestModalOpen} handleClose={toggleCreateRequestModal} />
        </div>
      </MobileSlideUpPanel>
      {/* Sort Action Modal */}
      <MobileActionModal
        isOpen={isSortModalOpen}
        onClose={() => setIsSortModalOpen(false)}
        title="Sort by"
        triggerRef={sortButtonRef} // Pass the ref
        options={[
          {
            name: 'Oldest First',
            onClick: () => handleSortSelect('oldest'),
            active: state.filters.sort === 'oldest',
          },
          {
            name: 'Newest First',
            onClick: () => handleSortSelect('newest'),
            active: state.filters.sort === 'newest',
          },
        ]}
      />
    </div>
  );
};

function MobileRequestsList({ readReport, trace, onShowDetails }: Props): JSX.Element {
  return <CustomBrowserColumn readReport={readReport} trace={trace} onShowDetails={onShowDetails} />;
}

export default MobileRequestsList;
